export const AVATAR_DIMENSION_PX = 256;
export const AVATAR_JPEG_QUALITY = 0.55;
export const AVATAR_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const AVATAR_MAX_SOURCE_BYTES = 5 * 1024 * 1024;

async function loadImage(src: string): Promise<HTMLImageElement> {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Unable to read image.'));
    img.src = src;
  });
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('Unable to read image.'));
    reader.readAsDataURL(blob);
  });
}

export async function renderAvatarBlob(
  render: (ctx: CanvasRenderingContext2D, size: number) => void,
  options?: {
    size?: number;
    quality?: number;
  },
): Promise<Blob> {
  const size = options?.size ?? AVATAR_DIMENSION_PX;
  const quality = options?.quality ?? AVATAR_JPEG_QUALITY;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Unable to prepare avatar image.');
  }

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, size, size);
  render(ctx, size);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((result) => resolve(result), 'image/jpeg', quality);
  });
  if (!blob) {
    throw new Error('Unable to prepare avatar image.');
  }
  return blob;
}

export async function optimizeAvatarFile(file: File): Promise<File> {
  const src = await blobToDataUrl(file);
  const image = await loadImage(src);
  const blob = await renderAvatarBlob((ctx, size) => {
    const sourceSize = Math.min(image.width, image.height);
    const sourceX = Math.max(0, (image.width - sourceSize) / 2);
    const sourceY = Math.max(0, (image.height - sourceSize) / 2);
    ctx.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, size, size);
  });

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'avatar';
  return new File([blob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}
